import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import { copyToClipboard } from '@/utils'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import QRCode from 'qrcode'
import { useCurrencySelection } from '@/contexts/AppStateContext'
import CustomButton from '@/components/CustomButton'
export default React.memo(function CurrencyDetails() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { selectedCurrency, currentNetwork } = useCurrencySelection()
  const [address, setAddress] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')

  const handleSelectNetwork = useCallback(() => {
    // 使用 replace 避免产生新的历史记录
    navigate('/recharge/select-network', { replace: true })
  }, [navigate])
  // 获取地址的方法
  const getAddress = useCallback(async (symbol: string, chainId: string) => {
    Toast.loading(t('获取地址中'))
    try {
      const data = await APIWeb.getAddress({
        symbol: symbol,
        chain_id: chainId
      })
      const newAddress = data.address || ''
      setAddress(newAddress)
      // 生成二维码
      if (newAddress) {
        try {
          const qrUrl = await QRCode.toDataURL(newAddress, {
            width: 192,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          })
          setQrCodeUrl(qrUrl)
        } catch (qrError) {
          console.error('生成二维码失败:', qrError)
        }
      }

    } catch (error) {
      console.error('获取地址失败:', error)
      Toast.fail({
        message: t('获取地址失败，请重试'),
        duration: 2000
      })
    } finally {
      Toast.clear()
    }
  }, [t])

  useEffect(() => {
    // 检查是否有选中的币种和网络
    if (!selectedCurrency || !currentNetwork) {
      Toast.info({
        message: t('请先选择网络'),
        onClose: () => navigate('/recharge/select-network', { replace: true })
      })
      return
    }

    // 检查网络信息是否完整
    if (!currentNetwork.symbol || !currentNetwork.chainId) {
      Toast.info({
        message: t('请先选择网络'),
        onClose: () => navigate('/recharge/select-network', { replace: true })
      })
      return
    }

    // 使用AppStateContext中的数据获取地址
    getAddress(currentNetwork.symbol, currentNetwork.chainId)
  }, [selectedCurrency, currentNetwork, getAddress, t, navigate])

  const handleCopyAddress = useCallback(() => {
    copyToClipboard(address)
    // 可以添加复制成功的提示
  }, [address])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={
          <div className="text-3.75 font-medium text-black truncate flex items-center">
            <img className='w-6 h-6 mr-2' src={selectedCurrency?.icon} />{selectedCurrency?.symbol}
          </div>
        }
        rightChildren={
          <div className='flex items-center'>
            <div className='w-6 h-6 mr-2'>
              <img src="/icon/upload.svg" />
            </div>
            <div className='w-6 h-6 mr-2'>
              <img src="/icon/question.svg" />
            </div>
            <div className='w-6 h-6' onClick={() => navigate('/recharge/deposit-records')}>
              <img src="/icon/record.svg" />
            </div>
          </div>
        }
      />

      <div className='px-6 pt-4'>
        {/* QR Code Section */}
        <div className="bg-white">
          <div className="mb-4">
            <div className="text-sm text-[#8B8B8B] text-center mb-4">{t('网络')}</div>
            <div onClick={handleSelectNetwork} className="text-black text-lg font-medium mr-2 flex justify-center items-center" >
              {currentNetwork?.symbol}<img src="/icon/select-down.svg" className="w-3 h-3 ml-2" />
            </div>
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-6">
            <div className="w-48 h-48 bg-gray-100 rounded-2xl flex items-center justify-center">
              {qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt="Address QR Code"
                  className="w-44 h-44 rounded-lg"
                />
              ) : (
                <div className="text-gray-500 text-sm text-center">
                  {address ? t('生成二维码中...') : t('请选择网络获取地址')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="bg-[#F3F3F3] mt-4 rounded-lg p-4 ">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div

                className="flex items-center space-x-1 mb-2 cursor-pointer"
              >
                <span className="text-xs text-[#666666]">
                  {t('选择网络')}
                </span>
              </div>
              <div className="text-sm text-black font-nomorl break-all pr-2">
                {address || '-'}
              </div>
            </div>
            <div>
              <CustomButton size='small' color='black' className='ml-2 rounded-full' onClick={handleCopyAddress}>
                <span className='text-xs'>
                  {t('复制')}
                </span>
              </CustomButton>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="bg-white rounded-lg mt-8 pb-8">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-black/36 text-xs">{t('充币账户')}</span>
              <span className="text-gray-900 font-medium text-xs">{t('交易账户')}</span>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-1">
                <span className="text-black/36 text-xs">{t('最小充币额')}</span>
                <div className="flex items-center justify-center text-1.5 text-black/36 border-[1px] border-solid border-black/36 rounded-full w-3 h-3">
                  !
                </div>
              </div>
              <span className="text-gray-900 font-medium text-xs">10 {selectedCurrency?.symbol}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-black/36 text-xs">{t('充币到账时间')}</span>
              <span className="text-gray-900 font-medium text-xs">{t('约X分钟', { minutes: 7 })}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-black/36 text-xs">{t('可提币时间')}</span>
              <span className="text-gray-900 font-medium text-xs">{t('约X分钟', { minutes: 2 })}</span>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
})